import React, { createContext, useContext, ReactNode } from 'react';

// Demo Context Interface
interface DemoContextType {
  isDemoMode: boolean;
  demoUser: {
    email: string;
    displayName: string;
    photoURL: string;
    uid: string;
  };
  demoPortfolio: {
    id: string;
    name: string;
    description: string;
    totalValue: number;
    totalPnL: number;
    pnlPercent: number;
  };
  demoPositions: any[];
  demoTransactions: any[];
  demoWatchlist: any[];
  demoNews: any[];
  canPerformAction: (action: string) => boolean;
}

const DemoContext = createContext<DemoContextType | undefined>(undefined);

export const useDemo = () => {
  const context = useContext(DemoContext);
  if (context === undefined) {
    // This should never happen since we wrap all routes with DemoProvider
    // But provide a safe fallback just in case
    console.warn('useDemo called outside DemoProvider, returning default non-demo context');
    return {
      isDemoMode: false,
      demoUser: { email: '', displayName: '', photoURL: '', uid: '' },
      demoPortfolio: { id: '', name: '', description: '', totalValue: 0, totalPnL: 0, pnlPercent: 0 },
      demoPositions: [],
      demoTransactions: [],
      demoWatchlist: [],
      demoNews: [],
      canPerformAction: () => true
    };
  }
  return context;
};

interface DemoProviderProps {
  children: ReactNode;
  isDemoMode?: boolean;
}

export function DemoProvider({ children, isDemoMode = false }: DemoProviderProps) {
  // Demo user data
  const demoUser = {
    email: 'demo@ailabquant.com',
    displayName: 'Demo User',
    photoURL: 'https://ui-avatars.com/api/?name=Demo+User&size=128&background=3b82f6&color=fff',
    uid: 'demo-user-123'
  };

  // Demo portfolio
  const demoPortfolio = {
    id: 'demo-portfolio-1',
    name: 'Growth Portfolio',
    description: 'Diversified tech and growth stocks',
    totalValue: 487650.25,
    totalPnL: 87650.25,
    pnlPercent: 21.9
  };

  // Demo positions
  const demoPositions = [
    {
      id: '1',
      symbol: 'AAPL',
      name: 'Apple Inc.',
      shares: 500,
      avgCost: 150.25,
      currentPrice: 189.50,
      marketValue: 94750,
      totalCost: 75125,
      unrealizedPnL: 19625,
      pnlPercent: 26.12,
      sector: 'Technology',
      allocation: 19.4
    },
    {
      id: '2',
      symbol: 'MSFT',
      name: 'Microsoft Corporation',
      shares: 300,
      avgCost: 280.50,
      currentPrice: 378.90,
      marketValue: 113670,
      totalCost: 84150,
      unrealizedPnL: 29520,
      pnlPercent: 35.08,
      sector: 'Technology',
      allocation: 23.3
    },
    {
      id: '3',
      symbol: 'GOOGL',
      name: 'Alphabet Inc.',
      shares: 400,
      avgCost: 105.75,
      currentPrice: 141.80,
      marketValue: 56720,
      totalCost: 42300,
      unrealizedPnL: 14420,
      pnlPercent: 34.09,
      sector: 'Technology',
      allocation: 11.6
    },
    {
      id: '4',
      symbol: 'NVDA',
      name: 'NVIDIA Corporation',
      shares: 250,
      avgCost: 220.30,
      currentPrice: 495.20,
      marketValue: 123800,
      totalCost: 55075,
      unrealizedPnL: 68725,
      pnlPercent: 124.79,
      sector: 'Technology',
      allocation: 25.4
    },
    {
      id: '5',
      symbol: 'AMZN',
      name: 'Amazon.com Inc.',
      shares: 350,
      avgCost: 125.40,
      currentPrice: 175.60,
      marketValue: 61460,
      totalCost: 43890,
      unrealizedPnL: 17570,
      pnlPercent: 40.03,
      sector: 'Consumer Cyclical',
      allocation: 12.6
    },
    {
      id: '6',
      symbol: 'TSLA',
      name: 'Tesla Inc.',
      shares: 150,
      avgCost: 205.80,
      currentPrice: 248.50,
      marketValue: 37275,
      totalCost: 30870,
      unrealizedPnL: 6405,
      pnlPercent: 20.75,
      sector: 'Consumer Cyclical',
      allocation: 7.6
    }
  ];

  // Demo transactions
  const demoTransactions = [
    {
      id: 'txn-1',
      date: '2025-01-02',
      time: '09:35:22',
      type: 'BUY',
      symbol: 'NVDA',
      shares: 50,
      price: 495.20,
      amount: 24760,
      status: 'Completed',
      portfolio: 'Growth Portfolio'
    },
    {
      id: 'txn-2',
      date: '2024-12-30',
      time: '14:22:15',
      type: 'SELL',
      symbol: 'META',
      shares: 100,
      price: 478.30,
      amount: 47830,
      status: 'Completed',
      portfolio: 'Growth Portfolio'
    },
    {
      id: 'txn-3',
      date: '2024-12-28',
      time: '10:15:08',
      type: 'BUY',
      symbol: 'AAPL',
      shares: 200,
      price: 189.50,
      amount: 37900,
      status: 'Completed',
      portfolio: 'Growth Portfolio'
    },
    {
      id: 'txn-4',
      date: '2024-12-20',
      time: '11:42:33',
      type: 'BUY',
      symbol: 'MSFT',
      shares: 100,
      price: 378.90,
      amount: 37890,
      status: 'Completed',
      portfolio: 'Growth Portfolio'
    },
    {
      id: 'txn-5',
      date: '2024-12-15',
      time: '13:28:47',
      type: 'BUY',
      symbol: 'GOOGL',
      shares: 150,
      price: 141.80,
      amount: 21270,
      status: 'Completed',
      portfolio: 'Growth Portfolio'
    }
  ];

  // Demo watchlist
  const demoWatchlist = [
    {
      id: 'w-1',
      symbol: 'META',
      name: 'Meta Platforms Inc.',
      price: 478.30,
      change: 8.25,
      changePercent: 1.76,
      volume: 12548000,
      marketCap: '1.21T',
      pe: 28.5,
      alert: 'Above 475'
    },
    {
      id: 'w-2',
      symbol: 'NFLX',
      name: 'Netflix Inc.',
      price: 512.80,
      change: -4.50,
      changePercent: -0.87,
      volume: 4235000,
      marketCap: '220.5B',
      pe: 45.2,
      alert: null
    },
    {
      id: 'w-3',
      symbol: 'AMD',
      name: 'Advanced Micro Devices',
      price: 145.60,
      change: 2.35,
      changePercent: 1.64,
      volume: 68450000,
      marketCap: '235.8B',
      pe: 52.3,
      alert: null
    },
    {
      id: 'w-4',
      symbol: 'COIN',
      name: 'Coinbase Global Inc.',
      price: 215.40,
      change: -12.80,
      changePercent: -5.61,
      volume: 18920000,
      marketCap: '52.1B',
      pe: null,
      alert: 'Below 220'
    },
    {
      id: 'w-5',
      symbol: 'SQ',
      name: 'Block Inc.',
      price: 78.90,
      change: 1.45,
      changePercent: 1.87,
      volume: 9340000,
      marketCap: '47.2B',
      pe: null,
      alert: null
    }
  ];

  // Demo news
  const demoNews = [
    {
      id: 'news-1',
      timestamp: '2025-01-02 10:30',
      headline: 'NVIDIA Announces New AI Chip Architecture',
      source: 'Reuters',
      category: 'Earnings' as const,
      sentiment: 'Bullish' as const,
      summary: 'NVIDIA unveiled its next-generation AI chip architecture, expected to deliver 3x performance improvements.',
      impact: 'High',
      relatedSymbols: ['NVDA', 'AMD', 'INTC']
    },
    {
      id: 'news-2',
      timestamp: '2025-01-02 09:15',
      headline: 'Federal Reserve Signals Potential Rate Cuts in H2 2025',
      source: 'Bloomberg',
      category: 'Central Bank' as const,
      sentiment: 'Bullish' as const,
      summary: 'Fed officials suggest inflation is cooling faster than expected, opening door for rate reductions.',
      impact: 'Very High',
      relatedSymbols: ['SPY', 'QQQ', 'TLT']
    },
    {
      id: 'news-3',
      timestamp: '2025-01-01 16:45',
      headline: 'EU Proposes New AI Regulation Framework',
      source: 'Financial Times',
      category: 'Regulation' as const,
      sentiment: 'Bearish' as const,
      summary: 'European Union unveils comprehensive AI regulations that could impact tech giants operating in the region.',
      impact: 'Medium',
      relatedSymbols: ['GOOGL', 'MSFT', 'META']
    },
    {
      id: 'news-4',
      timestamp: '2024-12-31 14:20',
      headline: 'Tesla Reports Record Q4 Deliveries',
      source: 'CNBC',
      category: 'Earnings' as const,
      sentiment: 'Bullish' as const,
      summary: 'Tesla delivered 484,500 vehicles in Q4, beating analyst expectations of 475,000.',
      impact: 'High',
      relatedSymbols: ['TSLA', 'GM', 'F']
    },
    {
      id: 'news-5',
      timestamp: '2024-12-30 11:30',
      headline: 'Geopolitical Tensions Rise in Middle East',
      source: 'Wall Street Journal',
      category: 'Geopolitics' as const,
      sentiment: 'Bearish' as const,
      summary: 'Escalating tensions could impact oil prices and global supply chains.',
      impact: 'High',
      relatedSymbols: ['XLE', 'USO', 'BNO']
    }
  ];

  // Function to check if user can perform actions in demo mode
  const canPerformAction = (action: string): boolean => {
    if (!isDemoMode) return true;
    
    // In demo mode, block these actions
    const blockedActions = [
      'trade',
      'buy',
      'sell',
      'save',
      'update',
      'delete',
      'create',
      'modify',
      'execute',
      'submit',
      'confirm'
    ];
    
    return !blockedActions.some(blocked => action.toLowerCase().includes(blocked));
  };

  const value: DemoContextType = {
    isDemoMode,
    demoUser,
    demoPortfolio,
    demoPositions,
    demoTransactions,
    demoWatchlist,
    demoNews,
    canPerformAction
  };

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}

